import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { installTriggerHandler } from './lib/hardware';
import DialogProvider from './components/DialogProvider';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AssetRegistration from './pages/AssetRegistration';
import AllAssets from './pages/AllAssets';
import AssetDetail from './pages/AssetDetail';
import LocationMaster from './pages/LocationMaster';
import LocationDetail from './pages/LocationDetail';
import AssetAudit from './pages/AssetAudit';
import AuditReport from './pages/AuditReport';
import AssetSearch from './pages/AssetSearch';
import RawInventory from './pages/RawInventory';
import UserManagement from './pages/UserManagement';
import Reports from './pages/Reports';
import HandheldSimulatorPage from './pages/HandheldSimulatorPage';
import HandheldGuide from './pages/HandheldGuide';
import AssetMovement from './pages/AssetMovement';
import ReaderManager from './pages/ReaderManager';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import ActivityLogs from './pages/ActivityLogs';
import UserGuide from './pages/UserGuide';
import Companies from './pages/Companies';
import SystemHealth from './pages/SystemHealth';
import Downloads from './pages/Downloads';
import Masters from './pages/Masters';
import HostLogin from './pages/host/HostLogin';
import HostCompanies from './pages/host/HostCompanies';

// Tiny route guard for host pages — separate from PrivateRoute (which checks
// the tenant token). Bounces unauthenticated host visitors to /host/login.
function HostPrivate({ children }) {
  const t = localStorage.getItem('identium_host_token');
  if (!t) return <Navigate to="/host/login" replace />;
  return children;
}

function App() {
  useEffect(() => installTriggerHandler(), []);
  return (
    <AuthProvider>
      <DialogProvider>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <Routes>
          {/* Host console — separate auth space, separate token, no shared layout */}
          <Route path="/host/login" element={<HostLogin />} />
          <Route path="/host/companies" element={<HostPrivate><HostCompanies /></HostPrivate>} />
          <Route path="/host" element={<Navigate to="/host/companies" replace />} />

          <Route path="/login" element={<Login />} />
          {/* Per-company login URL: /<slug>/login pre-fills the tenant so
              the user only types username + password. The slug is reserved
              against frontend route names on the backend (RESERVED_SLUGS in
              routes/system.js) so this can never collide with /dashboard,
              /settings, /companies, etc. — those literal routes always win
              in React Router specificity, but blocking the slugs avoids
              the inverse trap (tenant created with slug "settings"). */}
          <Route path="/:slug/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/asset-registration" element={<AssetRegistration />} />
            <Route path="/all-assets" element={<AllAssets />} />
            <Route path="/assets/:id" element={<AssetDetail />} />
            <Route path="/location-master" element={<LocationMaster />} />
            <Route path="/locations/:id" element={<LocationDetail />} />
            <Route path="/asset-movement" element={<AssetMovement />} />
            <Route path="/asset-audit" element={<AssetAudit />} />
            <Route path="/audit-report" element={<AuditReport />} />
            <Route path="/asset-search" element={<AssetSearch />} />
            <Route path="/raw-inventory" element={<RawInventory />} />
            <Route path="/handheld-simulator" element={<HandheldSimulatorPage />} />
            <Route path="/handheld-guide" element={<HandheldGuide />} />
            <Route path="/reader-manager" element={<ReaderManager />} />
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/activity-logs" element={<ActivityLogs />} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/system-health" element={<SystemHealth />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/:tab" element={<Settings />} />
            <Route path="/downloads" element={<Downloads />} />
            <Route path="/masters" element={<Masters />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </DialogProvider>
    </AuthProvider>
  );
}

export default App;
